import { seasons, groups, suppliers, contactPersons, brandOptions } from './stylesFixtureData';
import type { StyleRow } from './stylesFixtureData';

/**
 * Large-batch bulk-edit fixtures (DL-62594 epic — "Bulk edit styles: update execution,
 * feedback & performance", Testmo 1577259/1577264/1577267/1577272). The generic `generated`
 * rows in `styles.ts` cycle through all 6 `STYLE_STATES`, so only ~1/6 of any given slice is
 * editable — nowhere near the "~100 styles" the large-batch performance cases need in one
 * selection. These two blocks are real, persisted `stylesData` rows (not an ephemeral
 * `apiTransform` override) specifically so a bulk PUT against them round-trips through the same
 * find-by-id/merge/persist path as every other row, and the styles table's refetch after
 * "Apply changes" genuinely reflects the new values — an `apiTransform`-only override would
 * freeze the response at a fixed snapshot and could never show a post-update refetch.
 * Both sets are searchable by a unique name substring absent from every other fixture.
 *
 * Split out of `styles.ts` — same reason `itemsFixtureData.ts`/`bulkUpdateProbeFixtureData.ts`
 * were: keeps the handler file under the repo's 800-line cap (`.eslintrc.js` `max-lines`).
 */
const BULK_PERF_FIXTURE_COUNT = 100;
export const buildBulkPerformanceFixtureStyles = (): StyleRow[] =>
  Array.from({ length: BULK_PERF_FIXTURE_COUNT }, (_, i) => {
    const n = i + 1;
    const id = 93000 + n;
    return {
      id,
      name: `BulkPerfFixture Style ${n}`,
      // "ST-9" prefix (not a distinct letter prefix) deliberately keeps this fixture sorting
      // AFTER every generated ST-1xxx row under an ascending "Style number" sort — the sibling
      // `warnings-and-selection-limits.spec.ts` (epic dl-62592) depends on id N landing at sorted
      // row index N-1 for the first 150 rows; a customId that sorts earlier (e.g. a bare "BP-"
      // prefix, which is lexically before "ST-") would shift that spec's row positions.
      customId: `ST-${id}`,
      description: '',
      state: 'unpublished', // every row editable — this is the point of the fixture
      logo: '',
      // seasons[2] (SS25) — the large-batch spec's tests all bulk-set Season to "AW26"
      // (`TARGET_SEASON_NAME` there), so seeding SS25 here keeps that a genuine change for
      // every row. Not seasons[0] (AW25): the bulk-edit Season combo's rendered listbox does
      // not actually offer "AW25" as an option (see that spec's `pickSeasonOption` for detail).
      season: seasons[2],
      group: groups[0],
      brand: {
        id: brandOptions[0].id,
        customId: brandOptions[0].userDefinedId ?? '',
        name: brandOptions[0].name,
        contactPersonId: contactPersons[0].id,
        contactPersonName: contactPersons[0].name,
      },
      supplier: {
        id: suppliers[0].id,
        customId: suppliers[0].customId,
        name: suppliers[0].name,
        contactPersonId: contactPersons[1].id,
        contactPersonName: contactPersons[1].name,
      },
      categories: [],
      styleColors: [],
      customFields: [],
      properties: [],
      lastUpdated: '2026-01-01T00:00:00',
      readyForExport: false,
    };
  });

/**
 * A smaller, deliberately mixed-state batch (25 editable + 5 non-editable) for the large-batch
 * "exclusion at scale" adversarial case — proves the Edit-click narrowing (`filterEditableItems`,
 * see the sibling `warnings-and-selection-limits.spec.ts` header) still works correctly when the
 * selection is large, not just the 2–3 row case already covered by that epic.
 */
const BULK_PERF_MIXED_EDITABLE_COUNT = 25;
const BULK_PERF_MIXED_NON_EDITABLE_STATES = [
  'delivered',
  'cancelled',
  'deleted',
  'delivered',
  'cancelled',
];
export const buildBulkPerformanceMixedFixtureStyles = (): StyleRow[] => {
  const editable = Array.from({ length: BULK_PERF_MIXED_EDITABLE_COUNT }, (_, i) => {
    const n = i + 1;
    const id = 93100 + n;
    return {
      id,
      name: `BulkPerfMixed Style ${n}`,
      customId: `ST-${id}`, // see the ST-9 prefix note on the BulkPerfFixture builder above
      description: '',
      state: 'unpublished',
      logo: '',
      season: seasons[2], // SS25 — see the seasons[2] note on the BulkPerfFixture builder above
      group: groups[0],
      brand: {
        id: brandOptions[0].id,
        customId: brandOptions[0].userDefinedId ?? '',
        name: brandOptions[0].name,
        contactPersonId: contactPersons[0].id,
        contactPersonName: contactPersons[0].name,
      },
      supplier: {
        id: suppliers[0].id,
        customId: suppliers[0].customId,
        name: suppliers[0].name,
        contactPersonId: contactPersons[1].id,
        contactPersonName: contactPersons[1].name,
      },
      categories: [],
      styleColors: [],
      customFields: [],
      properties: [],
      lastUpdated: '2026-01-01T00:00:00',
      readyForExport: false,
    };
  });
  const nonEditable = BULK_PERF_MIXED_NON_EDITABLE_STATES.map((state, i) => {
    const n = BULK_PERF_MIXED_EDITABLE_COUNT + i + 1;
    const id = 93100 + n;
    return {
      ...editable[0],
      id,
      name: `BulkPerfMixed Style ${n}`,
      customId: `ST-${id}`,
      state,
    };
  });
  return [...editable, ...nonEditable];
};

/**
 * Dedicated, individually-named pairs for the "update execution & feedback" normal-size bulk-edit
 * suite (`update-execution-and-feedback.spec.ts`, DL-62594 epic). Each pair shares a unique name
 * substring (absent everywhere else) so the suite can select exactly those two rows via the
 * table's own search box rather than an index into a virtualised row list — `rowCheckboxes.nth(N)`
 * only resolves when row N is actually mounted, which a `@tanstack/react-virtual`-backed table
 * does not guarantee for an arbitrary N without first scrolling it into view. All rows are in an
 * editable state (`unpublished`).
 *
 * The "Noop" pair's seasons are chosen so the spec's adversarial test can bulk-set Season to
 * `TARGET_SEASON_NAME` ("AW26" there — the bulk-edit Season combo's rendered listbox does not
 * actually offer "AW25" as an option, despite it being first in the mock's `seasons` array, so
 * every test targets AW26 instead; see that spec's `pickSeasonOption`): B starts on AW26 already
 * (so bulk-setting to AW26 is a genuine no-op for it), A starts on AW25 (so the same bulk-set is a
 * genuine change for it) — the deliberate mix the "no-op detection" test needs. The "Happy"/
 * "Failure" pairs both start away from AW26 (SS25/SS26) so a bulk-set to it is unambiguously real.
 */
export const buildBulkFeedbackFixtureStyles = (): StyleRow[] => {
  const base = (id: number, name: string, season: (typeof seasons)[number]): StyleRow => ({
    id,
    name,
    customId: `ST-${id}`,
    description: '',
    state: 'unpublished',
    logo: '',
    season,
    group: groups[0],
    brand: {
      id: brandOptions[0].id,
      customId: brandOptions[0].userDefinedId ?? '',
      name: brandOptions[0].name,
      contactPersonId: contactPersons[0].id,
      contactPersonName: contactPersons[0].name,
    },
    supplier: {
      id: suppliers[0].id,
      customId: suppliers[0].customId,
      name: suppliers[0].name,
      contactPersonId: contactPersons[1].id,
      contactPersonName: contactPersons[1].name,
    },
    categories: [],
    styleColors: [],
    customFields: [],
    properties: [],
    lastUpdated: '2026-01-01T00:00:00',
    readyForExport: false,
  });

  return [
    base(94001, 'BulkFeedbackFixture Happy A', seasons[2]), // SS25
    base(94002, 'BulkFeedbackFixture Happy B', seasons[3]), // SS26
    base(94003, 'BulkFeedbackFixture Noop A', seasons[0]), // AW25 — a genuine change to AW26
    base(94004, 'BulkFeedbackFixture Noop B', seasons[1]), // AW26 — already TARGET_SEASON_NAME
    base(94005, 'BulkFeedbackFixture Failure A', seasons[2]), // SS25
    base(94006, 'BulkFeedbackFixture Failure B', seasons[3]), // SS26
  ];
};
